// Copyright (c) Docugami, Inc. All rights reserved.

import { INavLinkGroup, Nav } from "@fluentui/react"
import { dgFontFamily } from "components/common/Fonts"
import { DeveloperHubApiKeys } from "components/developerHub/DeveloperHubApiKeys"
import { backgroundGray, emptyGray, homeGray } from "components/shell/presentation/AppStyles"
import { Config } from "config"
import { dgIconNames } from "hooks/dgIcons"
import { DEVELOPER_HUB_PAGE, useNavigationService } from "hooks/navigationService"
import { useEffect, useMemo } from "react"
import { Redirect, useParams } from "react-router"
import styled from "styled-components"

const Root = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: row;
`

const ContentContainer = styled.div`
    flex: 1;
    display: flex;
    overflow: hidden;
`

const NavContainer = styled.div`
    display: flex;
    height: 100%;
    width: 300px;
    border: 1px solid ${emptyGray};
    overflow: hidden;
    flex-direction: column;
`

const NavHeader = styled.div`
    width: 100%;
    display: flex;
    height: 40px;
    align-items: center;
    justify-content: center;
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 700;
    background: ${backgroundGray};
    color: ${homeGray};
`

export enum DeveloperHubTarget {
    Tokens = 'tokens',
    ApiDocs = 'api-docs'
}

export const DeveloperHubPage = (): JSX.Element => {
    const params = useParams<{ target?: string }>();
    const target: DeveloperHubTarget | undefined = useMemo(() => Object.values(DeveloperHubTarget).find(x => x === params.target), [params.target]);

    const nav = useNavigationService();

    const groups: INavLinkGroup[] = [
        {
            links: [
                {
                    name: 'Tokens',
                    url: '',
                    key: DeveloperHubTarget.Tokens,
                    onClick: () => nav.navToDeveloperHub(DeveloperHubTarget.Tokens),
                    iconProps: {
                        iconName: dgIconNames[dgIconNames.DgDeveloperHubTokensIcon],
                        styles: {
                            root: {
                                width: 20,
                                height: 17.6,
                                '.primaryFill': {
                                    fill: 'black'
                                }
                            }
                        }
                    },
                },
                {
                    name: 'API Docs',
                    url: '',
                    key: DeveloperHubTarget.ApiDocs,
                    onClick: () => nav.navToDeveloperHub(DeveloperHubTarget.ApiDocs),
                    iconProps: {
                        iconName: 'Documentation',
                        styles: {
                            root: {
                                color: 'black !important',
                                ":hover": {
                                    color: 'black'
                                }
                            }
                        }
                    },
                }
            ]
        }
    ]

    function getContent() {
        switch (target) {
            case DeveloperHubTarget.Tokens: {
                return <DeveloperHubApiKeys />
            }

            default: {
                return <Redirect to={`/${DEVELOPER_HUB_PAGE}/${DeveloperHubTarget.Tokens}`} />
            }
        }
    }

    useEffect(() => {
        if (target === DeveloperHubTarget.ApiDocs) {
            window.open(Config.apiDocumentationUrl, '_blank', 'noreferrer');
        }
    }, [target]);

    return (
        <Root>
            <NavContainer>
                <NavHeader>DEVELOPER PLAYGROUND</NavHeader>
                <Nav
                    selectedKey={target}
                    groups={groups}
                    styles={{
                        root: {
                            padding: '10px 5px'
                        },
                        link: {
                            borderRadius: 5,
                            color: 'black'
                        },
                    }}
                />

            </NavContainer>
            <ContentContainer>
                {getContent()}
            </ContentContainer>
        </Root>
    )
}
